import React, { useMemo, useState, useEffect } from 'react';
import ReactApexChart from 'react-apexcharts';
import { useAnalytics } from '@Context/AnalyticsContext';
import { ApexOptions } from 'apexcharts';

const COLOR_PALETTE = [
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#06B6D4',
  '#F472B6',
  '#A3A3A3',
] as const;

const AnalyticsBarChart: React.FC = () => {
  const {
    rawData,
    modelUsageData,
    userUsageData,
    featureUsageData,
    filters,
    loading,
  } = useAnalytics();

  const [focusedModelIndex, setFocusedModelIndex] = useState<number | null>(
    null
  );

  useEffect(() => {
    setFocusedModelIndex(null);
  }, [filters.user, filters.feature, filters.model]);

  const chartData = useMemo(() => {
    if (filters.user) {
      return {
        title: `Feature Usage for ${filters.user}`,
        subtitle: 'Token usage by feature',
        categories: featureUsageData.map((item) =>
          item.feature
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (l) => l.toUpperCase())
        ),
        series: [
          {
            name: 'Tokens',
            data: featureUsageData.map((item) => item.tokens),
          },
        ],
        colors: ['#8B5CF6'],
      };
    }

    if (filters.feature) {
      const topUsers = userUsageData.slice(0, 10);
      return {
        title: `User Usage for ${filters.feature.replace(/_/g, ' ')}`,
        subtitle: 'Token usage by user',
        categories: topUsers.map((item) => item.username),
        series: [
          {
            name: 'Tokens',
            data: topUsers.map((item) => item.tokens),
          },
        ],
        colors: ['#10B981'],
      };
    }

    if (filters.model) {
      const topUsers = userUsageData.slice(0, 10);
      return {
        title: `User Usage for ${filters.model.replace(/^us\.(anthropic|amazon)\./, '')}`,
        subtitle: 'Token usage by user',
        categories: topUsers.map((item) => item.username),
        series: [
          {
            name: 'Tokens',
            data: topUsers.map((item) => item.tokens),
          },
        ],
        colors: ['#F59E0B'],
      };
    }

    const modelTokenMap = new Map<
      string,
      { id: string; name: string; tokens: number }
    >();

    rawData.forEach((item) => {
      const { modelId } = item.model;
      const existing = modelTokenMap.get(modelId);
      if (existing) {
        existing.tokens += item.tokens.total;
      } else {
        modelTokenMap.set(modelId, {
          id: modelId,
          name: item.model.name || modelId,
          tokens: item.tokens.total,
        });
      }
    });

    const topModels = Array.from(modelTokenMap.values())
      .sort((a, b) => b.tokens - a.tokens)
      .slice(0, 8);

    const categories = topModels.map((m) =>
      m.name.replace(/^us\.(anthropic|amazon)\./, '')
    );

    const features = featureUsageData.map((f) => f.feature);
    const series = features.map((feature) => {
      const data = topModels.map((model) => {
        return rawData
          .filter(
            (r) => r.model.modelId === model.id && r.featureKey === feature
          )
          .reduce((sum, r) => sum + r.tokens.total, 0);
      });

      return {
        name: feature
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (l) => l.toUpperCase()),
        data,
      };
    });

    return {
      title: 'Model Usage by Feature',
      subtitle: 'Token usage per model split by feature',
      categories,
      series,
      colors: features.map((_, i) => COLOR_PALETTE[i % COLOR_PALETTE.length]),
    };
  }, [rawData, modelUsageData, userUsageData, featureUsageData, filters]);

  const displayedChartData = useMemo(() => {
    if (focusedModelIndex === null) return chartData;

    const focusedCategory = chartData.categories[focusedModelIndex] || '';
    const focusedSeries = chartData.series.map((s) => {
      const value = Array.isArray(s.data) ? s.data[focusedModelIndex] || 0 : 0;
      return { ...s, data: [value] };
    });

    return {
      ...chartData,
      title: `${chartData.title} — ${focusedCategory}`,
      subtitle: `Focused view: ${focusedCategory}`,
      categories: [focusedCategory],
      series: focusedSeries,
    };
  }, [chartData, focusedModelIndex]);

  const chartOptions = useMemo(
    (): ApexOptions => ({
      chart: {
        type: 'bar',
        height: 400,
        stacked: true,
        events: {
          dataPointSelection: (_event, _chartContext, config) => {
            if (focusedModelIndex !== null) return;

            const idx =
              typeof config.dataPointIndex === 'number'
                ? config.dataPointIndex
                : null;
            if (idx !== null) {
              setFocusedModelIndex(idx);
            }
          },
        },
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: false,
            zoom: false,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: false,
          },
        },
      },
      colors: displayedChartData.colors,
      legend: { show: false },
      plotOptions: {
        bar: {
          borderRadius: 4,
          horizontal: false,
          columnWidth: '60%',
        },
      },
      dataLabels: { enabled: false },
      xaxis: {
        categories: displayedChartData.categories,
        labels: {
          style: { fontSize: '12px', colors: '#6B7280' },
          rotate: -45,
          maxHeight: 100,
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        title: {
          text: 'Values',
          style: { fontSize: '12px', fontWeight: 600, color: '#374151' },
        },
      },
      grid: { borderColor: '#E5E7EB', strokeDashArray: 3 },
      tooltip: {
        y: {
          formatter: (value) =>
            typeof value === 'number'
              ? `${value.toLocaleString()} tokens`
              : `${value}`,
        },
      },
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'light',
          type: 'vertical',
          shadeIntensity: 0.25,
          gradientToColors: undefined,
          inverseColors: false,
          opacityFrom: 0.85,
          opacityTo: 0.85,
        },
      },
    }),
    [displayedChartData, focusedModelIndex]
  );

  if (loading) {
    return (
      <div className="rounded-lg bg-white p-6 shadow">
        <div className="animate-pulse">
          <div className="mb-2 h-4 w-1/3 rounded bg-gray-300"></div>
          <div className="mb-4 h-3 w-1/2 rounded bg-gray-200"></div>
          <div className="h-96 rounded bg-gray-200"></div>
        </div>
      </div>
    );
  }

  if (chartData.categories.length === 0) {
    return (
      <div className="rounded-lg bg-white p-6 shadow">
        <div className="py-12 text-center">
          <div className="mb-2 text-lg text-gray-400">No Data Available</div>
          <div className="text-sm text-gray-500">
            No analytics data found for the selected filters.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-white p-6 shadow">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {displayedChartData.title}
        </h3>
        <p className="text-sm text-gray-600">{displayedChartData.subtitle}</p>
      </div>

      {focusedModelIndex !== null && (
        <div className="mb-4">
          <button
            onClick={() => setFocusedModelIndex(null)}
            className="rounded border border-gray-200 bg-white px-3 py-1 text-sm text-gray-700 hover:bg-gray-50"
          >
            ← Back to all models
          </button>
        </div>
      )}

      <ReactApexChart
        options={chartOptions}
        series={displayedChartData.series}
        type="bar"
        height={400}
      />
    </div>
  );
};

export default AnalyticsBarChart;
